import $http from '@/utils/axios';
import type { ApiResponse } from '@/types/common/response';
import type { AgentConversation, AgentMessage } from '@/types/agent/agent';
import type { AgentModelConfig } from '@/types/agent/model';

export interface ChatModelsResponse {
  models: AgentModelConfig[];
  defaultModelId?: string;
  [key: string]: any;
}

export interface SendMessageOptions {
  attachments?: any[];
  regenerate?: boolean;
  streaming?: boolean;
  useMCP?: boolean;
  mcpTools?: string[];
  maxTokens?: number;
  model?: string;
  enableReasoning?: boolean;
  apiURL?: string;
  apiKey?: string;
  maxHistoryMessages?: number;
  [key: string]: any;
}

class AgentService {
  protected $http: typeof $http;
  public resource: string;

  constructor() {
    this.$http = $http;
    this.resource = 'agent';
  }

  /**
   * 发送消息
   */
  sendMessage(message: string, conversationId: string | null = null, options: SendMessageOptions = {}): Promise<any> {
    const body: Record<string, any> = {
      message: message || '',
      conversationId,
      attachments: options.attachments || [],
      regenerate: options.regenerate === true,
      streaming: options.streaming !== false,
      useMCP: options.useMCP || false,
      mcpTools: options.mcpTools || [],
      maxTokens: options.maxTokens || 32 * 1024,
      model: options.model || 'deepseek-v3',
      enableReasoning: options.enableReasoning || false,
    };
    if (options.apiURL) {
      body.apiURL = String(options.apiURL).trim();
    }
    if (options.apiKey) {
      body.apiKey = String(options.apiKey).trim();
    }
    const mh = Number(options.maxHistoryMessages);
    if (Number.isFinite(mh) && mh >= 1) {
      body.maxHistoryMessages = Math.min(500, Math.floor(mh));
    }
    return this.$http.post(`/${this.resource}/chat`, body);
  }

  /**
   * 上传 Agent 附件到 COS
   */
  uploadAttachment(file: File): Promise<ApiResponse<any>> {
    const fd = new FormData();
    fd.append('file', file);
    return this.$http.post(`/${this.resource}/upload`, fd, {
      headers: { 'Content-Type': undefined }
    });
  }

  /**
   * 获取对话列表
   */
  getConversations(): Promise<ApiResponse<AgentConversation[]>> {
    return this.$http.get(`/${this.resource}/conversations`);
  }

  /**
   * 获取启用中的 Agent 模型列表
   */
  getChatModels(): Promise<ApiResponse<ChatModelsResponse>> {
    return this.$http.get(`/${this.resource}/chat-models`);
  }

  /**
   * 获取对话的消息列表
   */
  getMessages(conversationId: string | number): Promise<ApiResponse<AgentMessage[]>> {
    return this.$http.get(`/${this.resource}/conversations/${conversationId}/messages`);
  }

  /**
   * 删除对话
   */
  deleteConversation(conversationId: string | number): Promise<ApiResponse<any>> {
    return this.$http.delete(`/${this.resource}/conversations/${conversationId}`);
  }

  /**
   * 清空当前用户全部会话
   */
  deleteAllConversations(): Promise<ApiResponse<any>> {
    return this.$http.delete(`/${this.resource}/conversations/all`);
  }

  /**
   * 更新对话标题
   */
  updateConversationTitle(conversationId: string | number, title: string): Promise<ApiResponse<any>> {
    return this.$http.put(`/${this.resource}/conversations/${conversationId}/title`, {
      title
    });
  }

  /**
   * 更新会话偏好
   */
  updateConversationSession(conversationId: string | number, body: Record<string, any>): Promise<ApiResponse<any>> {
    return this.$http.put(`/${this.resource}/conversations/${conversationId}/context`, body);
  }

  /**
   * 删除单条消息
   */
  deleteMessage(messageId: string | number): Promise<ApiResponse<any>> {
    return this.$http.delete(`/${this.resource}/messages/${messageId}`);
  }
}

export default new AgentService();
